/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// Backstage Sidebar (sidenav) overrides.
//
// Dividers: the nav has none. The SidebarDivider elements are simply not
// rendered in plugins/app-module-navigation/src/components/sidebar/sidebar.tsx (a
// CSS-hide would be fragile: Backstage's divider has no stable withStyles
// name, so its readable JSS class only exists in dev builds, not production).
//
// Backstage uses MUI v4 withStyles for these components, so JSS keys
// (`root`, `drawer`, `highlightable`, `selected`, ...) override via the
// theme `overrides` map under the component keys below.

import { NS_SHADOW_ELEVATED_2 } from '@ns/plugin-ns-theme';

const sidebarItemBase = {
  root: {
    borderRadius: 'var(--ns-radius-m)',
    // The nav item is 32 high, against 48 in core.
    height: 32,
    minHeight: 32,
    // An inactive item has regular weight (400). core-components hard-sets the
    // label to bold. The weight sits on the item and the label inherits it, so
    // the selected state (700) makes the label bold without a nested class
    // selector (which crashes the MUI v4 theme-override render).
    fontWeight: 400,
    color: 'var(--ns-nav-fg)',
    // The ring sits outside the item, as on every other ns-theme field. The
    // scroll area keeps 4 of room around the items so it is not clipped.
    '&:focus-visible': {
      outline:
        'var(--ns-border-width-medium) solid var(--ns-color-border-focus)',
      outlineOffset: 'var(--ns-border-width-thin)',
    },
  },

  // Core fixes the label at 110 wide and bold. It takes the rest of the pill
  // instead, truncates with an ellipsis, and inherits the weight of the item
  // (400, or 700 when selected).
  label: {
    width: 'auto',
    flex: 1,
    minWidth: 0,
    fontSize: 16,
    lineHeight: 1.25,
    fontWeight: 'inherit',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // The pill fills the slot the drawer gives it and has no outer margin: the
  // drawer pads 16 on each side, so the slot is 224 wide open. The pill pads 8
  // inside, and the icon is 24, so the icon centre lands on x=36 from the
  // drawer edge (16 + 8 + 24/2), the same axis as the logo tile. `!important`
  // beats the fixed drawer width core sets on `open` inside `@media (sm)`.
  open: {
    width: '100% !important',
    margin: 0,
    paddingInline: 'var(--ns-space-2)',
    boxSizing: 'border-box' as const,
  },

  // Compact, the slot is 40 wide. The pill fills it and centres the 24 icon,
  // which again puts the icon centre on x=36 (16 + 40/2).
  closed: {
    width: '100%',
    margin: 0,
    justifyContent: 'center',
  },

  // Core makes the icon box 72 wide with a negative margin. Here it is the
  // icon itself, with a gap of 8 to the label, so an item without an icon
  // keeps its label on the same x.
  iconContainer: {
    width: 24,
    minWidth: 24,
    marginRight: 'var(--ns-space-2)',
  },

  closedItemIcon: {
    width: 24,
  },

  // Core always renders this box open, 48 wide plus a margin, even when it has
  // nothing in it. Without the collapse the label would lose 56 on the right.
  secondaryAction: {
    width: 'auto',
    marginRight: 0,
    '&:empty': {
      display: 'none',
    },
  },

  // Hover and pressed on an inactive item. The pill shape comes from
  // root.borderRadius, so the state covers the pill, not the whole row.
  // :active comes after :hover: during a click both match with equal
  // specificity, and the last rule wins.
  highlightable: {
    '&:hover': {
      background: 'var(--ns-nav-item-hover-bg)',
      color: 'var(--ns-nav-fg)',
    },
    '&:active': {
      background: 'var(--ns-nav-item-pressed-bg)',
      color: 'var(--ns-nav-fg)',
    },
  },

  // core-components sets `highlighted` on the FULL-WIDTH `item-with-submenu`
  // wrapper (without radius) on hover over a submenu parent, which covered the
  // whole row. Transparent, so only the inset pill on the button (highlightable)
  // shows the hover, consistent with regular items.
  highlighted: {
    background: 'transparent',
  },

  // The selected pill applies to regular items and to the PARENT of a submenu
  // with an active child (core sets `classes.selected` there via isActive). On
  // the button variant (Beta), core's `buttonItem {background:none}` and `root`
  // (color/weight) have equal specificity but a later source order, so a plain
  // selected rule loses. Hence !important on background, color and weight, so
  // the pill also renders on the submenu parent. (Links lack buttonItem and work
  // anyway, and !important is harmless there.)
  selected: {
    background: 'var(--ns-nav-item-selected-bg) !important',
    color: 'var(--ns-nav-fg-selected) !important',
    fontWeight: '700 !important',
    // Core draws a 3px left indicator bar via `&$root` (0,2,0). The selected
    // state here is only the pill.
    borderLeft: 'none !important',
    // Core's indicator also makes a selected compact item 72 wide and shifts
    // its icon by 3. MUI merges theme overrides into core's nested rules, so
    // each one is cancelled here, or the active icon would move.
    '&$closed': {
      width: '100% !important',
    },
    '& $iconContainer': {
      marginLeft: 0,
    },
    '& $closedItemIcon': {
      paddingRight: 0,
    },
    '&:hover': {
      background: 'var(--ns-nav-item-selected-hover-bg) !important',
      color: 'var(--ns-nav-fg-selected) !important',
    },
    // Also !important, or the base selected background wins over pressed.
    '&:active': {
      background: 'var(--ns-nav-item-selected-pressed-bg) !important',
      color: 'var(--ns-nav-fg-selected) !important',
    },
  },

  // Core renders the submenu chevron only when NOT hovered (`!isHoveredOn`), so
  // it drops out of the DOM on hover. That conditional arrow is hidden, and a
  // persistent chevron is drawn as `::after` on the button instead. Scoping to
  // the `buttonItem` class key is exact: only the submenu parent is a button
  // variant, and regular nav items are links without that class (so they have
  // no chevron). currentColor makes the chevron follow the item state
  // (near-black when inactive, blue when selected or an active parent).
  submenuArrow: {
    display: 'none',
  },
  buttonItem: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      // At the right of the pill, over the end of the label.
      right: 14,
      top: '50%',
      width: 7,
      height: 7,
      borderTop: '2px solid currentColor',
      borderRight: '2px solid currentColor',
      // A square with a top and right border is a chevron pointing right.
      // translateY centres it vertically, rotate sets the chevron angle.
      transform: 'translateY(-50%) rotate(45deg)',
    },
  },
};

const sidebarDrawerBase = {
  drawer: {
    backgroundColor: 'var(--ns-color-bg-elevated-2)',
    borderRight: '1px solid var(--ns-color-border-default)',
    // Its own property, not a layer in one box-shadow list with the edge: in
    // dark the elevation shadow is `none`, which voids the whole list.
    boxShadow: NS_SHADOW_ELEVATED_2,
    // The drawer hosts the four slots (brand, the scroll area with primary and
    // utility, control) and gives each the same inset: 24 top and bottom, 16 at
    // the sides, so every slot is 224 wide open and 40 compact, as in the
    // design. A slot has no outer margin of its own and there is no gap: the
    // space between slots is the slot's own margin. alignItems stretch gives
    // each slot the full content width (core already makes the drawer a flex
    // column). border-box keeps the padding inside the width core sets (256 or
    // 72); otherwise the drawer grows by 32.
    paddingBlock: 'var(--ns-space-6)',
    paddingInlineStart: 'var(--ns-space-4)',
    // The border takes 1 px inside the border-box, the inset gives it back.
    paddingInlineEnd: 'calc(var(--ns-space-4) - 1px)',
    boxSizing: 'border-box' as const,
    alignItems: 'stretch',
  },
};

// Note: BackstageSidebarDivider cannot be themed via MUI v4 overrides (its
// withStyles call lacks a stable name) and a global CSS-hide would only work
// in dev. The dividers are therefore not rendered at all (see
// plugins/app-module-navigation/src/components/sidebar/sidebar.tsx).

export const BackstageSidebarOverrides = sidebarDrawerBase;
export const BackstageSidebarItemOverrides = sidebarItemBase;
